import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Activity, RefreshCw, Database, Link2, Clock, UserX,
  CheckCircle2, AlertTriangle, Play, Mail, FlaskConical, Server, HeartPulse, Users,
  Stethoscope, AlertCircle, XCircle, Droplet, ClipboardList,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import ImpactSeedPanel from '../components/admin/ImpactSeedPanel';
import HealthCacheCard from '../components/admin/HealthCacheCard';
import CacheHealthCard from '../components/admin/CacheHealthCard';
import WarmupTrendCard from '../components/admin/WarmupTrendCard';

const StatusPill = ({ status }) => {
  const clean = status === 'clean';
  return (
    <Badge
      className={clean ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}
      data-testid="data-health-status"
    >
      {clean ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <AlertTriangle className="h-3 w-3 mr-1" />}
      {clean ? 'All clean' : 'Drift detected'}
    </Badge>
  );
};

const MetricCard = ({ icon: Icon, label, value, testId, tone = 'default', children }) => {
  const tones = {
    default: 'bg-white border-slate-200',
    warn: 'bg-amber-50 border-amber-200',
    good: 'bg-emerald-50 border-emerald-200',
  };
  return (
    <Card className={`${tones[tone]}`} data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Icon className="h-4 w-4" />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-slate-900">{value}</div>
        {children}
      </CardContent>
    </Card>
  );
};

/**
 * DriftNowStrip — top-of-page actionable callout that lists every
 * non-zero drift signal with a one-click remediation button.
 * Mirrors the pattern of `<FailingNowStrip>` on the Code Health
 * page: surfaces what needs fixing right now so admins don't have
 * to scroll through audit panels to find their next action.
 *
 * Self-hides via the parent's `data.status === 'drift'` gate, so
 * this component is only ever rendered when there's something to do.
 */
function DriftNowStrip({
  data, driftRows,
  backfilling, purging, assigning,
  onBackfill, onPurgeSessions, onAssignRoles,
}) {
  const signals = [];

  if (data.timestamps.total_missing > 0) {
    signals.push({
      key: 'timestamps',
      icon: Database,
      title: 'Missing timestamps',
      summary: `${data.timestamps.total_missing.toLocaleString()} rows across ${driftRows.length} collection${driftRows.length === 1 ? '' : 's'}`,
      detail: driftRows.slice(0, 4).map(r => `${r.collection} (${r.missing.toLocaleString()})`).join(' · ')
        + (driftRows.length > 4 ? ` · +${driftRows.length - 4} more` : ''),
      actionLabel: 'Apply backfill',
      actionTestId: 'drift-now-backfill-btn',
      onAction: onBackfill,
      busy: backfilling,
    });
  }

  if (data.sessions.expired > 0) {
    signals.push({
      key: 'sessions',
      icon: Clock,
      title: 'Expired sessions',
      summary: `${data.sessions.expired.toLocaleString()} expired sessions still in the database`,
      detail: `Out of ${data.sessions.total.toLocaleString()} total — purging recovers index space and cleans up the audit feed.`,
      actionLabel: 'Purge expired',
      actionTestId: 'drift-now-purge-btn',
      onAction: onPurgeSessions,
      busy: purging,
    });
  }

  if (data.roles.without_roles_count > 0) {
    signals.push({
      key: 'roles',
      icon: UserX,
      title: 'Users without roles',
      summary: `${data.roles.without_roles_count.toLocaleString()} users have no active role`,
      detail: `Out of ${data.roles.total_users.toLocaleString()} total — assigning the default role unblocks them on next login.`,
      actionLabel: 'Assign default role',
      actionTestId: 'drift-now-assign-roles-btn',
      onAction: onAssignRoles,
      busy: assigning,
    });
  }

  const orphanCount = (data.orphans.orphan_cohort_refs || 0) + (data.orphans.orphan_program_refs || 0);
  if (orphanCount > 0) {
    // Orphan refs require manual repair (cohort/program IDs vary
    // by tenant) — link to the table below instead of offering a
    // one-click fix that could mask intent.
    signals.push({
      key: 'orphans',
      icon: Link2,
      title: 'Orphan references',
      summary: `${orphanCount.toLocaleString()} rows pointing at deleted cohorts/programs`,
      detail: `${data.orphans.orphan_cohort_refs} cohort refs · ${data.orphans.orphan_program_refs} program refs. Inspect the Orphan References card below to triage.`,
      actionLabel: null,  // intentionally no auto-fix
    });
  }

  // Defensive — should never render when signals is empty because
  // the parent gates on `data.status === 'drift'`, but cover the
  // edge case where status is stale.
  if (signals.length === 0) return null;

  return (
    <Card
      className="border-amber-300 bg-amber-50/60 ring-1 ring-amber-200"
      data-testid="drift-now-strip"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-amber-900">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          Drift right now
          <Badge className="bg-amber-200 text-amber-900 ml-1" data-testid="drift-now-count">
            {signals.length} signal{signals.length === 1 ? '' : 's'}
          </Badge>
        </CardTitle>
        <CardDescription className="text-amber-800/80">
          Each row below has a one-click remediation. Action buttons run in <strong>apply</strong> mode —
          the dry-run / detail tables below the metric cards are still here for deeper triage.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {signals.map((s) => (
          <div
            key={s.key}
            data-testid={`drift-now-${s.key}`}
            className="bg-white border border-amber-200 rounded-lg p-3 flex items-start gap-3"
          >
            <div className="p-1.5 bg-amber-100 rounded shrink-0">
              <s.icon className="h-4 w-4 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900">{s.title}</p>
              <p className="text-xs text-amber-800 font-medium">{s.summary}</p>
              {s.detail && (
                <p className="text-[11px] text-slate-600 mt-0.5 break-words">{s.detail}</p>
              )}
            </div>
            {s.actionLabel && (
              <Button
                size="sm"
                onClick={s.onAction}
                disabled={s.busy}
                data-testid={s.actionTestId}
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
              >
                {s.busy
                  ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <Play className="h-3.5 w-3.5 mr-1.5" />}
                {s.busy ? 'Running…' : s.actionLabel}
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const DataHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [purging, setPurging] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetPreview, setResetPreview] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [deployment, setDeployment] = useState(null);
  const [deploymentLoading, setDeploymentLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/data-health/summary');
      setData(res.data);
    } catch (err) {
      logger.error('[DataHealth] summary failed', err);
      toast.error(err?.response?.data?.detail || 'Failed to load data health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runBackfill = async (apply) => {
    setBackfilling(true);
    try {
      const res = await api.post(
        `/admin/data-health/backfill-timestamps?apply=${apply ? 'true' : 'false'}`
      );
      if (apply) {
        toast.success(`Backfill applied: ${res.data.updated} documents updated`);
      } else {
        toast.info(`Dry run: ${res.data.total_would_update} documents would be updated`);
      }
      await load();
    } catch (err) {
      logger.error('[DataHealth] backfill failed', err);
      toast.error(err?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  const purgeStaleSessions = async (apply) => {
    setPurging(true);
    try {
      const res = await api.post(
        `/admin/data-health/purge-stale-sessions?apply=${apply ? 'true' : 'false'}`
      );
      if (apply) {
        toast.success(`Purged ${res.data.deleted} expired sessions`);
      } else {
        toast.info(`Dry run: ${res.data.would_delete} sessions would be deleted`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const assignDefaultRoles = async (apply) => {
    setAssigning(true);
    try {
      const res = await api.post(
        `/admin/data-health/assign-default-roles?apply=${apply ? 'true' : 'false'}`
      );
      if (apply) {
        const failures = res.data.failures?.length || 0;
        toast.success(
          `Assigned default role to ${res.data.assigned} users` +
          (failures ? ` (${failures} failed — see console)` : '')
        );
        if (failures) logger.warn('[DataHealth] role assign failures', res.data.failures);
      } else {
        toast.info(`Dry run: ${res.data.would_assign} users would get the default role`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Assign failed');
    } finally {
      setAssigning(false);
    }
  };

  const sendDigestNow = async () => {
    setDigestSending(true);
    try {
      const res = await api.post('/admin/data-health/send-digest-now');
      if (res.data.sent > 0) {
        toast.success(`Digest email sent to ${res.data.sent} super-admin(s)`);
      } else {
        toast.info(`Digest not sent: ${res.data.skipped_reason || 'no recipients'}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setDigestSending(false);
    }
  };

  const openResetDialog = async () => {
    setResetDialog(true);
    setResetPreview(null);
    setResetBusy(true);
    try {
      const res = await api.post('/admin/data-health/reset-acme-demo');
      setResetPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load reset preview');
      setResetDialog(false);
    } finally {
      setResetBusy(false);
    }
  };

  const applyReset = async () => {
    setResetBusy(true);
    try {
      const res = await api.post('/admin/data-health/reset-acme-demo?apply=true');
      toast.success(
        `Acme Industries reset: wiped ${res.data.wiped}, inserted ${res.data.inserted}`
      );
      setResetDialog(false);
      setResetPreview(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    } finally {
      setResetBusy(false);
    }
  };

  // ── Missing Middle demo-story seeder (Behavior leak + Adoption
  // maturity gap) — targets the org being previewed, else caller's. ──
  const { viewAsOrg } = useAuth();
  const [mmSeedBusy, setMmSeedBusy] = useState(false);
  const seedMissingMiddle = async () => {
    const org = viewAsOrg?.id;
    if (!window.confirm(
      `Seed the "Missing Middle" demo story${org ? ` for ${viewAsOrg?.name || org}` : ' for your current org'}?\n\n`
      + 'This makes BEHAVIOR both this quarter\'s steepest value leak (~-22%) and the maturity-constrained stage (pillar_5 weakest). It writes a prior quarter snapshot + adjusts maturity pillar scores. Idempotent.',
    )) return;
    setMmSeedBusy(true);
    try {
      const qs = org ? `?organization_id=${org}` : '';
      const res = await api.post(`/admin/data-health/seed-missing-middle-story${qs}`);
      const d = res.data;
      toast.success(
        `Missing Middle story seeded for ${d.organization_id} — Behavior leak + ${d.maturity?.weakest_pillar || 'pillar_5'} weakest (maturity ${d.maturity?.overall_score ?? '–'}).`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Seed failed');
    } finally {
      setMmSeedBusy(false);
    }
  };

  // ── Targeted PIE-only seed (doesn't touch other Acme data) ──────
  const [pieSeedBusy, setPieSeedBusy] = useState(false);
  const [pieSeedDialog, setPieSeedDialog] = useState(false);
  const [pieSeedPreview, setPieSeedPreview] = useState(null);

  const openPieSeedDialog = async () => {
    setPieSeedDialog(true);
    setPieSeedPreview(null);
    setPieSeedBusy(true);
    try {
      const res = await api.post('/admin/data-health/seed-pie-personas');
      setPieSeedPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load PIE seed preview');
      setPieSeedDialog(false);
    } finally {
      setPieSeedBusy(false);
    }
  };

  const applyPieSeed = async () => {
    setPieSeedBusy(true);
    try {
      const res = await api.post('/admin/data-health/seed-pie-personas?apply=true');
      toast.success(
        `PIE personas seeded: wiped ${res.data.wiped}, inserted ${res.data.inserted}`
      );
      setPieSeedDialog(false);
      setPieSeedPreview(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Seed failed');
    } finally {
      setPieSeedBusy(false);
    }
  };

  const [capSeedBusy, setCapSeedBusy] = useState(false);
  const [capSeedDialog, setCapSeedDialog] = useState(false);
  const [capSeedTargets, setCapSeedTargets] = useState(null);
  const openCapSeedDialog = async () => {
    setCapSeedDialog(true);
    setCapSeedTargets(null);
    try {
      const res = await api.post('/admin/data-health/seed-capability-inventory'); // dry-run
      setCapSeedTargets(res.data.targets || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not preview capability seed');
    }
  };
  const seedCapabilityInventory = async () => {
    setCapSeedBusy(true);
    try {
      const res = await api.post('/admin/data-health/seed-capability-inventory?apply=true');
      const parts = (res.data.results || []).map(r => r.error ? `${r.org_name || r.organization_id}: ${r.error}` : `${r.org_name}: ${r.members}m/${r.roles}r`).join(' · ');
      toast.success(`Capability inventory seeded — ${parts}`);
      setCapSeedDialog(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Capability seed failed');
    } finally {
      setCapSeedBusy(false);
    }
  };


  // ── PIE diagnostics — green/yellow/red self-check ──────────────
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagnosticsExpanded, setDiagnosticsExpanded] = useState(false);
  const [diagnosticsBusy, setDiagnosticsBusy] = useState(false);

  // Auto-probe silently on mount so the header pill shows current health
  // without forcing the admin to click anything. Failures are swallowed
  // (a non-super-admin viewing this page wouldn't get a 200 anyway, and
  // we don't want a toast every page-load).
  useEffect(() => {
    let cancelled = false;
    api.get('/personal/diagnostics')
      .then((r) => { if (!cancelled) setDiagnostics(r.data); })
      .catch(() => { /* swallow — pill stays neutral */ });
    return () => { cancelled = true; };
  }, []);

  const runDiagnostics = async () => {
    setDiagnosticsBusy(true);
    try {
      const res = await api.get('/personal/diagnostics');
      setDiagnostics(res.data);
      setDiagnosticsExpanded(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Diagnostics failed to run');
    } finally {
      setDiagnosticsBusy(false);
    }
  };

  const fetchDeployment = async () => {
    setDeploymentLoading(true);
    try {
      const res = await api.get('/admin/data-health/deployment');
      setDeployment(res.data);
    } catch (err) {
      logger.error('[DataHealth] deployment fetch failed', err);
    } finally {
      setDeploymentLoading(false);
    }
  };

  useEffect(() => { fetchDeployment(); }, []);

  if (loading && !data) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" />
      </div>
    );
  }

  if (!data) return null;

  const driftRows = (data.timestamps?.per_collection || []).filter(r => r.missing > 0);

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Data Health"
        description="Audit and backfill checks for your platform data — drift detection, remediation, and last-checked transparency."
        theme={__pageTheme}
        icon={HeartPulse}
        testId="data-health-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6" data-testid="data-health-page">
      <div className="flex items-start justify-between">
        <div>
                    <p className="text-sm text-slate-600">
            On-demand drift detection + one-click remediation. Last checked:{' '}
            {new Date(data.checked_at).toLocaleString()}
          </p>
          {diagnostics && (
            <PIEDiagnosticsPill
              diagnostics={diagnostics}
              expanded={diagnosticsExpanded}
              onClick={() => setDiagnosticsExpanded((v) => !v)}
            />
          )}
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          data-testid="data-health-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <Button
          onClick={sendDigestNow}
          disabled={digestSending}
          variant="outline"
          className="ml-2"
          data-testid="send-digest-now-btn"
        >
          <Mail className={`h-4 w-4 mr-2 ${digestSending ? 'animate-pulse' : ''}`} />
          {digestSending ? 'Sending…' : 'Send digest now'}
        </Button>
        <Button
          onClick={openResetDialog}
          variant="outline"
          className="ml-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
          data-testid="reset-acme-demo-btn"
          title="Wipe and re-seed the Acme Industries demo tenant"
        >
          <FlaskConical className="h-4 w-4 mr-2" />
          Reset Acme Demo
        </Button>
        <Button
          onClick={openPieSeedDialog}
          variant="outline"
          className="ml-2 border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
          data-testid="seed-pie-personas-btn"
          title="Seed only the Seertech Mirror personas (200 synthetic users + 5 showcase personas). Doesn't affect other Acme demo data."
        >
          <Users className="h-4 w-4 mr-2" />
          Seed PIE Personas
        </Button>
        <Button
          onClick={seedMissingMiddle}
          disabled={mmSeedBusy}
          variant="outline"
          className="ml-2 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
          data-testid="seed-missing-middle-btn"
          title="Seed the Missing Middle demo story (Behavior value leak + Adoption maturity gap) for the org you're previewing. Idempotent."
        >
          <Droplet className={`h-4 w-4 mr-2 ${mmSeedBusy ? 'animate-pulse' : ''}`} />
          {mmSeedBusy ? 'Seeding…' : 'Seed Missing Middle'}
        </Button>
        <Button
          onClick={openCapSeedDialog}
          disabled={capSeedBusy}
          variant="outline"
          className="ml-2 border-teal-300 text-teal-700 hover:bg-teal-50 hover:text-teal-800"
          data-testid="seed-capability-inventory-btn"
          title="Wipe + reseed the Capability Inventory demo across the configured orgs (resolved by name)."
        >
          <ClipboardList className={`h-4 w-4 mr-2 ${capSeedBusy ? 'animate-pulse' : ''}`} />
          {capSeedBusy ? 'Seeding…' : 'Seed Capability'}
        </Button>
        <Button
          onClick={runDiagnostics}
          disabled={diagnosticsBusy}
          variant="outline"
          className="ml-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
          data-testid="run-pie-diagnostics-btn"
          title="Run a self-check across PIE's critical paths: env vars, MongoDB, action ranking engine, LLM connectivity, Resend"
        >
          <Stethoscope className={`h-4 w-4 mr-2 ${diagnosticsBusy ? 'animate-pulse' : ''}`} />
          {diagnosticsBusy ? 'Running…' : 'Run PIE Diagnostics'}
        </Button>
      </div>

      {diagnostics && diagnosticsExpanded && (
        <PIEDiagnosticsPanel
          diagnostics={diagnostics}
          onClose={() => setDiagnosticsExpanded(false)}
          onRerun={runDiagnostics}
          busy={diagnosticsBusy}
        />
      )}

      {data.status === 'drift' && (
        <DriftNowStrip
          data={data}
          driftRows={driftRows}
          backfilling={backfilling}
          purging={purging}
          assigning={assigning}
          onBackfill={() => runBackfill(true)}
          onPurgeSessions={() => purgeStaleSessions(true)}
          onAssignRoles={() => assignDefaultRoles(true)}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={Database}
          label="Missing timestamps"
          value={data.timestamps.total_missing.toLocaleString()}
          testId="metric-missing-timestamps"
          tone={data.timestamps.total_missing ? 'warn' : 'good'}
        />
        <MetricCard
          icon={Link2}
          label="Orphan refs"
          value={(data.orphans.orphan_cohort_refs + data.orphans.orphan_program_refs).toLocaleString()}
          testId="metric-orphan-refs"
          tone={(data.orphans.orphan_cohort_refs + data.orphans.orphan_program_refs) ? 'warn' : 'good'}
        >
          <div className="text-xs text-slate-600 mt-2">
            {data.orphans.orphan_cohort_refs} cohort • {data.orphans.orphan_program_refs} program
          </div>
        </MetricCard>
        <MetricCard
          icon={Clock}
          label="Expired sessions"
          value={data.sessions.expired.toLocaleString()}
          testId="metric-expired-sessions"
          tone={data.sessions.expired ? 'warn' : 'good'}
        >
          <div className="text-xs text-slate-600 mt-2">of {data.sessions.total.toLocaleString()} total</div>
        </MetricCard>
        <MetricCard
          icon={UserX}
          label="Users without roles"
          value={data.roles.without_roles_count.toLocaleString()}
          testId="metric-users-without-roles"
          tone={data.roles.without_roles_count ? 'warn' : 'good'}
        >
          <div className="text-xs text-slate-600 mt-2">of {data.roles.total_users.toLocaleString()} users</div>
        </MetricCard>
      </div>

      <Card data-testid="timestamp-drift-card">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-teal-600" />
                Timestamp drift by collection
              </CardTitle>
              <CardDescription>
                Documents missing <code>created_at</code> / <code>updated_at</code>.
                Backfill uses semantic fallback fields (e.g., <code>timestamp</code> on
                baselines), then the MongoDB _id ObjectId, then now().
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={backfilling}
                onClick={() => runBackfill(false)}
                data-testid="backfill-dry-run-btn"
              >
                Dry run
              </Button>
              <Button
                size="sm"
                disabled={backfilling || data.timestamps.total_missing === 0}
                onClick={() => runBackfill(true)}
                data-testid="backfill-apply-btn"
              >
                <Play className="h-3.5 w-3.5 mr-1.5" />
                {backfilling ? 'Running…' : 'Apply backfill'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {driftRows.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
              No drift — every registered collection has complete timestamps.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="drift-table">
                <thead className="text-slate-500 border-b">
                  <tr>
                    <th className="text-left py-2 font-medium">Collection</th>
                    <th className="text-right py-2 font-medium">Total</th>
                    <th className="text-right py-2 font-medium">Missing</th>
                    <th className="text-left py-2 font-medium pl-4">Fallback fields</th>
                  </tr>
                </thead>
                <tbody>
                  {driftRows.map(row => (
                    <tr key={row.collection} className="border-b last:border-0">
                      <td className="py-2 font-mono text-slate-700">{row.collection}</td>
                      <td className="py-2 text-right text-slate-600">{row.total.toLocaleString()}</td>
                      <td className="py-2 text-right">
                        <span className="text-amber-700 font-semibold">{row.missing.toLocaleString()}</span>
                      </td>
                      <td className="py-2 pl-4 text-xs text-slate-500">
                        {row.fallback_fields.length ? row.fallback_fields.join(', ') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="orphan-refs-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-teal-600" />
            Orphan references
          </CardTitle>
          <CardDescription>
            Data rows still pointing at cohorts/programs that have been deleted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(data.orphans.examples || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">
              <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-emerald-500" />
              No orphan references.
            </div>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.orphans.examples.map((ex) => {
                const stableKey = ex.component_id || ex.program_id ||
                  `${(ex.bad_cohort_ids || []).join(',')}|${(ex.bad_program_ids || []).join(',')}`;
                return (
                  <li key={stableKey} className="flex justify-between border-b pb-2">
                    <span className="font-mono text-slate-600">
                      {ex.component_id || ex.program_id}
                    </span>
                    <span className="text-xs text-amber-700">
                      {ex.bad_cohort_ids?.length ? `${ex.bad_cohort_ids.length} bad cohort refs` : ''}
                      {ex.bad_program_ids?.length ? ` ${ex.bad_program_ids.length} bad program refs` : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card data-testid="sessions-card">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-teal-600" />
                Stale session cleanup
              </CardTitle>
              <CardDescription>
                Delete expired entries from <code>user_sessions</code>.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={purging}
                onClick={() => purgeStaleSessions(false)}
                data-testid="purge-dry-run-btn"
              >
                Dry run
              </Button>
              <Button
                size="sm"
                disabled={purging || data.sessions.expired === 0}
                onClick={() => purgeStaleSessions(true)}
                data-testid="purge-apply-btn"
              >
                <Play className="h-3.5 w-3.5 mr-1.5" />
                {purging ? 'Purging…' : 'Purge expired'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6 text-sm">
            <div>
              <div className="text-slate-500">Total sessions</div>
              <div className="text-xl font-bold text-slate-900">{data.sessions.total.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-slate-500">Expired</div>
              <div className={`text-xl font-bold ${data.sessions.expired ? 'text-amber-700' : 'text-emerald-700'}`}>
                {data.sessions.expired.toLocaleString()}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {data.roles.without_roles_count > 0 && (
        <Card data-testid="users-without-roles-card">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <UserX className="h-5 w-5 text-teal-600" />
                  Users without roles ({data.roles.without_roles_count})
                </CardTitle>
                <CardDescription>
                  These users have no active RBAC role assignment. Auto-remediation assigns the
                  default role (via the same helper used at registration / Google sign-in).
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={assigning}
                  onClick={() => assignDefaultRoles(false)}
                  data-testid="assign-roles-dry-run-btn"
                >
                  Dry run
                </Button>
                <Button
                  size="sm"
                  disabled={assigning}
                  onClick={() => assignDefaultRoles(true)}
                  data-testid="assign-roles-apply-btn"
                >
                  <Play className="h-3.5 w-3.5 mr-1.5" />
                  {assigning ? 'Assigning…' : 'Assign default role'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-slate-700 space-y-1">
              {data.roles.without_roles.map(email => (
                <li key={email} className="font-mono text-xs">{email}</li>
              ))}
              {data.roles.without_roles_count > data.roles.without_roles.length && (
                <li className="text-xs text-slate-500 italic">
                  + {data.roles.without_roles_count - data.roles.without_roles.length} more
                </li>
              )}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Impact-chain story seed (super-admin) */}
      <ImpactSeedPanel />
      <HealthCacheCard />
      <CacheHealthCard />
      <WarmupTrendCard />

      {/* Deployment Health — build-time env vs runtime origin sanity check */}
      <Card data-testid="deployment-health-card">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-slate-800">
                <Server className="h-5 w-5 text-slate-600" /> Deployment health
              </CardTitle>
              <CardDescription>
                Build-time env vars vs runtime origin — catches custom-domain
                drift, CORS mismatch, and bad deploys before users notice.
              </CardDescription>
            </div>
            <Button
              size="sm" variant="outline"
              onClick={fetchDeployment}
              disabled={deploymentLoading}
              data-testid="deployment-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${deploymentLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {deploymentLoading && !deployment && (
            <div className="text-center text-sm text-slate-500 py-6">Checking…</div>
          )}
          {deployment && (() => {
            const baked = process.env.REACT_APP_BACKEND_URL || '(unset)';
            const origin = typeof window !== 'undefined' ? window.location.origin : '';
            let resolvedBase = baked;
            try {
              const bu = new URL(baked);
              const bakedIsPreview = bu.hostname.endsWith('.preview.emergentagent.com');
              const originIsPreview = origin.includes('.preview.emergentagent.com');
              const originIsLocal = origin.startsWith('http://localhost');
              if (bakedIsPreview && !originIsPreview && !originIsLocal) resolvedBase = origin;
              if (bu.origin === origin) resolvedBase = baked;
            } catch { resolvedBase = origin || baked; }
            const drift = resolvedBase !== baked;
            const rows = [
              { label: 'Current window origin', value: origin, tone: 'neutral' },
              { label: 'Build-time REACT_APP_BACKEND_URL', value: baked,
                tone: drift ? 'warn' : 'ok',
                detail: drift ? 'Baked URL does not match the current origin' : 'Matches current origin' },
              { label: 'Resolved API base (in use now)', value: resolvedBase,
                tone: drift ? 'warn' : 'ok',
                detail: drift ? 'Runtime fallback active — same-origin routing' : 'Using baked value' },
              { label: 'Backend CORS_ORIGINS', value: deployment.cors.is_wildcard ? '*' : deployment.cors.origins_env.join(', '),
                tone: deployment.cors.is_wildcard ? 'warn' : 'ok',
                detail: deployment.cors.is_wildcard
                  ? 'Wildcard + allow_credentials=True is browser-rejected for credentialed cross-origin preflight (only safe same-origin)'
                  : 'Explicit allow-list' },
              { label: 'Request origin header accepted by CORS',
                value: deployment.request.origin_allowed_by_cors ? 'yes' : 'no',
                tone: deployment.request.origin_allowed_by_cors ? 'ok' : 'error',
                detail: deployment.request.origin_header || '(no origin sent on same-origin request)' },
              { label: 'MongoDB reachable', value: deployment.backend_env.has_mongo_url ? 'configured' : 'missing',
                tone: deployment.backend_env.has_mongo_url ? 'ok' : 'error' },
              { label: 'Resend (email) configured', value: deployment.backend_env.has_resend_key ? 'yes' : 'no',
                tone: deployment.backend_env.has_resend_key ? 'ok' : 'warn' },
              { label: 'Emergent LLM key configured', value: deployment.backend_env.has_emergent_llm_key ? 'yes' : 'no',
                tone: deployment.backend_env.has_emergent_llm_key ? 'ok' : 'warn' },
            ];
            const toneStyle = { ok: 'border-emerald-200 bg-emerald-50 text-emerald-800',
                                warn: 'border-amber-200 bg-amber-50 text-amber-800',
                                error: 'border-rose-200 bg-rose-50 text-rose-800',
                                neutral: 'border-slate-200 bg-slate-50 text-slate-700' };
            const toneIcon = { ok: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />,
                               warn: <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />,
                               error: <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />,
                               neutral: <div className="h-3.5 w-3.5" /> };
            return (
              <div className="space-y-2">
                {rows.map((r) => (
                  <div
                    key={r.label}
                    className={`flex flex-wrap items-start justify-between gap-3 rounded-md border px-3 py-2 ${toneStyle[r.tone]}`}
                    data-testid={`deployment-row-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
                  >
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <span className="mt-0.5">{toneIcon[r.tone]}</span>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold">{r.label}</div>
                        {r.detail && <div className="text-[11px] opacity-75 mt-0.5">{r.detail}</div>}
                      </div>
                    </div>
                    <div className="text-xs font-mono break-all text-right max-w-[60%]">
                      {r.value}
                    </div>
                  </div>
                ))}
                {deployment.issues?.length > 0 && (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                    <div className="text-xs font-semibold text-amber-800 mb-1">
                      Backend-side issues ({deployment.issues.length})
                    </div>
                    <ul className="text-xs text-amber-900 space-y-1">
                      {deployment.issues.map((i, idx) => (
                        <li key={`${i.code || 'iss'}-${idx}`}>
                          <span className="font-mono mr-1">[{i.severity}]</span>
                          <span className="font-semibold">{i.code}</span> — {i.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Reset Acme Demo Dialog */}
      <Dialog open={resetDialog} onOpenChange={(o) => { if (!o && !resetBusy) { setResetDialog(false); setResetPreview(null); } }}>
        <DialogContent className="max-w-xl" data-testid="reset-acme-demo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-indigo-600" />
              Reset Acme Industries demo tenant
            </DialogTitle>
            <DialogDescription>
              This wipes every <code>*_acme_demo_*</code> record and re-seeds the
              demo tenant from scratch. Use it between recording sessions so
              every video starts from an identical, pristine Acme state. No
              other organisation is affected.
            </DialogDescription>
          </DialogHeader>

          {resetBusy && !resetPreview && (
            <div className="py-6 text-center text-sm text-slate-500">
              <RefreshCw className="h-4 w-4 inline animate-spin mr-2" />
              Checking what will change…
            </div>
          )}

          {resetPreview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-center">
                  <div className="text-xs text-rose-700 font-semibold uppercase tracking-wide">
                    Will wipe
                  </div>
                  <div className="text-2xl font-bold text-rose-800" data-testid="reset-wipe-count">
                    {resetPreview.total_would_wipe}
                  </div>
                  <div className="text-[11px] text-rose-600">rows across all collections</div>
                </div>
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-center">
                  <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">
                    Will seed
                  </div>
                  <div className="text-2xl font-bold text-emerald-800" data-testid="reset-seed-count">
                    {resetPreview.total_would_seed}
                  </div>
                  <div className="text-[11px] text-emerald-600">pristine rows</div>
                </div>
              </div>

              {resetPreview.wipe_preview?.length > 0 && (
                <div className="rounded-md border border-slate-200 bg-white max-h-52 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left text-slate-500">
                        <th className="px-3 py-1.5">Collection</th>
                        <th className="px-3 py-1.5 text-right">Rows to wipe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resetPreview.wipe_preview.map((w) => (
                        <tr key={w.collection} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 text-slate-800 font-mono">{w.collection}</td>
                          <td className="px-3 py-1.5 text-right text-slate-700">{w.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="text-xs text-slate-500 pt-1">
                Org: <strong>{resetPreview.org_name}</strong> ({resetPreview.org_id})
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setResetDialog(false); setResetPreview(null); }}
              disabled={resetBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={applyReset}
              disabled={resetBusy || !resetPreview}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="reset-acme-confirm-btn"
            >
              {resetBusy ? 'Working…' : 'Wipe & Re-seed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed Capability Inventory Dialog */}
      <Dialog open={capSeedDialog} onOpenChange={(o) => { if (!o && !capSeedBusy) { setCapSeedDialog(false); setCapSeedTargets(null); } }}>
        <DialogContent className="max-w-md" data-testid="seed-capability-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-teal-600" />
              Seed / reset Capability Inventory
            </DialogTitle>
            <DialogDescription>
              This <strong>wipes and reseeds</strong> the capability inventory (skills, roles,
              members, skills &amp; assignments) for the orgs below, assigning members across their
              named cohorts. <strong className="text-rose-700">Any uploaded (real) capability data for these orgs will be replaced.</strong>
            </DialogDescription>
          </DialogHeader>

          {capSeedTargets === null ? (
            <div className="py-6 text-center text-sm text-slate-500">
              <RefreshCw className="h-4 w-4 inline animate-spin mr-2" /> Resolving target orgs…
            </div>
          ) : capSeedTargets.length === 0 ? (
            <div className="py-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3">
              No orgs in this environment match the seed config (Seertech Solutions, Acme Industries).
            </div>
          ) : (
            <div className="space-y-2" data-testid="cap-seed-targets">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Will wipe + reseed</p>
              {capSeedTargets.map(t => (
                <div key={t} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  <ClipboardList className="h-3.5 w-3.5 text-teal-600" />
                  <span className="font-mono text-xs">{t}</span>
                  <span className="ml-auto text-[11px] text-slate-400">48 members · named cohorts</span>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { if (!capSeedBusy) { setCapSeedDialog(false); setCapSeedTargets(null); } }} data-testid="cap-seed-cancel">
              Cancel
            </Button>
            <Button
              onClick={seedCapabilityInventory}
              disabled={capSeedBusy || !capSeedTargets || capSeedTargets.length === 0}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="cap-seed-confirm">
              {capSeedBusy ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Seeding…</> : 'Wipe & reseed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed PIE Personas Dialog */}
      <Dialog
        open={pieSeedDialog}
        onOpenChange={(o) => {
          if (!o && !pieSeedBusy) {
            setPieSeedDialog(false);
            setPieSeedPreview(null);
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="seed-pie-personas-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Seed Seertech Mirror personas
            </DialogTitle>
            <DialogDescription>
              Reseeds the synthetic PIE population for the Acme demo tenant only.
              Other Acme demo data (programs, business cases, analytics) is left
              untouched.
            </DialogDescription>
          </DialogHeader>

          {pieSeedBusy && !pieSeedPreview && (
            <div className="py-6 text-center text-sm text-slate-500">
              <RefreshCw className="h-4 w-4 inline animate-spin mr-2" />
              Checking what will change…
            </div>
          )}

          {pieSeedPreview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-center">
                  <div className="text-xs text-rose-700 font-semibold uppercase tracking-wide">
                    Will wipe
                  </div>
                  <div
                    className="text-2xl font-bold text-rose-800"
                    data-testid="pie-seed-wipe-count"
                  >
                    {pieSeedPreview.wiped}
                  </div>
                  <div className="text-[11px] text-rose-600">existing PIE users</div>
                </div>
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-center">
                  <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">
                    Will seed
                  </div>
                  <div
                    className="text-2xl font-bold text-emerald-800"
                    data-testid="pie-seed-insert-count"
                  >
                    {pieSeedPreview.inserted}
                  </div>
                  <div className="text-[11px] text-emerald-600">
                    synthetic + showcase
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-500 pt-1">
                Target org: <strong className="font-mono">{pieSeedPreview.org_id}</strong>
              </div>
              <div className="text-xs text-slate-500">
                Includes 5 showcase personas (Hana, Ben, Reza, Saoirse, Cyrus)
                plus 200 synthetic users distributed across role groups and
                archetypes.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setPieSeedDialog(false); setPieSeedPreview(null); }}
              disabled={pieSeedBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={applyPieSeed}
              disabled={pieSeedBusy || !pieSeedPreview}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="seed-pie-confirm-btn"
            >
              {pieSeedBusy ? 'Working…' : 'Seed PIE personas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageShell>
  );
};

export default DataHealthPage;

/**
 * PIEDiagnosticsPanel — render the green/yellow/red rows returned by
 * GET /api/personal/diagnostics. Each row shows a status dot, the
 * human-readable label, the one-line message, and (where available)
 * round-trip latency. Headline tile up top summarises overall status
 * + counts; collapse-to-close to keep the page from getting noisy.
 */
function PIEDiagnosticsPanel({ diagnostics, onClose, onRerun, busy }) {
  const { overall, counts, checks, elapsed_ms } = diagnostics;
  const overallTone = {
    green: { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'All systems healthy' },
    yellow: { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700', label: 'Degraded — some non-blocking warnings' },
    red: { bg: 'bg-rose-50', border: 'border-rose-200', dot: 'bg-rose-500', text: 'text-rose-700', label: 'Critical issues detected' },
  }[overall] || { bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400', text: 'text-slate-700', label: 'Unknown' };

  return (
    <div
      className={`mb-4 rounded-xl border-2 ${overallTone.border} ${overallTone.bg} overflow-hidden`}
      data-testid="pie-diagnostics-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/60 bg-white border border-slate-200">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`h-3 w-3 rounded-full ${overallTone.dot} flex-shrink-0`} aria-hidden />
          <div className="min-w-0">
            <p className={`text-sm font-bold ${overallTone.text}`}>PIE Diagnostics — {overallTone.label}</p>
            <p className="text-[11px] text-slate-500">
              {counts.green} green · {counts.yellow} yellow · {counts.red} red · ran in {elapsed_ms}ms
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            onClick={onRerun}
            disabled={busy}
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            data-testid="pie-diagnostics-rerun"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${busy ? 'animate-spin' : ''}`} />
            Re-run
          </Button>
          <Button
            onClick={onClose}
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            data-testid="pie-diagnostics-close"
          >
            Hide
          </Button>
        </div>
      </div>
      <div className="divide-y divide-slate-200/60">
        {checks.map((c) => (
          <DiagnosticsRow key={c.id} check={c} />
        ))}
      </div>
    </div>
  );
}

function PIEDiagnosticsPill({ diagnostics, expanded, onClick }) {
  const { overall, counts } = diagnostics;
  const tone = {
    green: { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'Healthy' },
    yellow: { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700', label: 'Degraded' },
    red: { bg: 'bg-rose-50', border: 'border-rose-300', dot: 'bg-rose-500 animate-pulse', text: 'text-rose-700', label: 'Critical' },
  }[overall] || { bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400', text: 'text-slate-600', label: 'Unknown' };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="pie-diagnostics-pill"
      className={`mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full border ${tone.border} ${tone.bg} text-xs ${tone.text} hover:shadow-sm transition-shadow`}
      title={expanded ? 'Hide diagnostics panel' : 'Show diagnostics panel'}
    >
      <span className={`h-2 w-2 rounded-full ${tone.dot}`} aria-hidden />
      <span className="font-semibold">PIE: {tone.label}</span>
      <span className="text-slate-500 font-normal">
        {counts.green}🟢 · {counts.yellow}🟡 · {counts.red}🔴
      </span>
      <span className="text-slate-400">{expanded ? '↑ hide' : '↓ details'}</span>
    </button>
  );
}

function DiagnosticsRow({ check }) {  const tone = {
    green: { dot: 'bg-emerald-500', icon: CheckCircle2, iconColor: 'text-emerald-600' },
    yellow: { dot: 'bg-amber-500', icon: AlertCircle, iconColor: 'text-amber-600' },
    red: { dot: 'bg-rose-500', icon: XCircle, iconColor: 'text-rose-600' },
  }[check.status] || { dot: 'bg-slate-400', icon: AlertCircle, iconColor: 'text-slate-500' };
  const Icon = tone.icon;

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 bg-white border border-slate-200"
      data-testid={`pie-diag-row-${check.id}`}
    >
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${tone.iconColor}`} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-800">{check.label}</span>
          {typeof check.latency_ms === 'number' && check.latency_ms > 0 && (
            <span className="text-[10px] text-slate-400 font-mono">{check.latency_ms}ms</span>
          )}
        </div>
        <p className="text-xs text-slate-600 mt-0.5">{check.message}</p>
        {check.details && Object.keys(check.details).length > 0 && (
          <details className="mt-1.5">
            <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600">
              show details
            </summary>
            <pre className="mt-1.5 text-[10px] bg-slate-100 rounded p-2 overflow-auto text-slate-700 font-mono">
              {JSON.stringify(check.details, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
